if (!customElements.get("m-product-videos")) {
  customElements.define(
    "m-product-videos",
    class MProductVideos extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.modal = new MinimogTheme.Modal();
        this.videoTemplates = Array.from(this.querySelectorAll("[data-product-video-template]"));
        this.navigationTemplate = this.querySelector("[data-product-video-navigation]");
        this.handleClick = this.handleClick.bind(this);
        this.handleExternalClick = this.handleExternalClick.bind(this);
        this.handleModalClick = this.handleModalClick.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.productContainer = this.closest("product-info");
        this.addEventListener("click", this.handleClick);
        this.productContainer?.addEventListener("click", this.handleExternalClick);
        this.modal.modal.addEventListener("click", this.handleModalClick);
        this.unsubscribeModal = window.MinimogEvents.subscribe("ON_MODAL_CLOSED", (modal) => {
          if (modal === this.modal) {
            window.removeEventListener("keydown", this.handleKeyDown);
            this.stopAllVideos();
            this.destroySlider();
            if (this.activeTrigger?.isConnected) this.activeTrigger.focus();
            this.activeTrigger = null;
          }
        });
      }

      disconnectedCallback() {
        this.removeEventListener("click", this.handleClick);
        this.productContainer?.removeEventListener("click", this.handleExternalClick);
        this.modal.modal.removeEventListener("click", this.handleModalClick);
        window.removeEventListener("keydown", this.handleKeyDown);
        if (this.unsubscribeModal) this.unsubscribeModal();
        if (this.modal.modal.isConnected) this.modal.close();
        this.stopAllVideos();
        this.destroySlider();
        this.initialized = false;
      }

      pauseVideo(video) {
        if (!video) return;

        video.pause();
        try {
          video.currentTime = 0;
        } catch (error) {
          // The video metadata may not be available yet.
        }
        video.preload = "none";
      }

      stopAllVideos() {
        this.gallery?.querySelectorAll("video").forEach((video) => this.pauseVideo(video));
      }

      pauseInactiveVideos(activeSlide) {
        this.gallery?.querySelectorAll(".m-product-videos__slide").forEach((slide) => {
          if (slide !== activeSlide) this.pauseVideo(slide.querySelector("video"));
        });
      }

      loadActiveVideo(activeSlide) {
        if (!activeSlide) return;

        let video = activeSlide.querySelector("video");
        if (!video) {
          const template = this.videoTemplates[Number(activeSlide.dataset.videoIndex)];
          const media = template?.content.firstElementChild?.cloneNode(true);
          if (!media) return;

          activeSlide.querySelector(".m-product-videos__media").appendChild(media);
          video = activeSlide.querySelector("video");
          if (!video) return;

          video.preload = "metadata";
          video.load();
        } else {
          video.preload = "metadata";
        }

        video.play().catch(() => {});
      }

      updateNavigation(slider) {
        if (!this.previousButton || !this.nextButton) return;

        this.previousButton.disabled = slider.isBeginning;
        this.nextButton.disabled = slider.isEnd;
      }

      handleVideoSlideChange(slider = this.slider) {
        if (!slider) return;

        const activeSlide = slider.slides[slider.activeIndex];
        this.pauseInactiveVideos(activeSlide);
        this.loadActiveVideo(activeSlide);
        this.updateNavigation(slider);
      }

      destroySlider() {
        if (this.slider && !this.slider.destroyed) this.slider.destroy(true, true);
        this.slider = null;
        this.gallery = null;
        this.previousButton = null;
        this.nextButton = null;
      }

      handleModalClick(event) {
        if (event.target === this.modal.modal || event.target.closest(".m-modal--close")) {
          this.stopAllVideos();
        }
      }

      handleKeyDown(event) {
        if (!this.modal.modal.isConnected) return;
        if (event.key === "Escape") {
          this.stopAllVideos();
          return;
        }

        if (
          event.target instanceof Element &&
          event.target.closest("input, textarea, select, button, video, [contenteditable='true']")
        ) {
          return;
        }
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          this.slider?.slidePrev();
        }
        if (event.key === "ArrowRight") {
          event.preventDefault();
          this.slider?.slideNext();
        }
      }

      handleClick(event) {
        const trigger = event.target.closest("[data-product-video-trigger]");
        if (!trigger || !this.contains(trigger)) return;

        const initialIndex = this.videoTemplates.findIndex(
          (template) => template.dataset.productVideoTemplate === trigger.dataset.productVideoTrigger
        );
        if (initialIndex < 0) return;

        this.openVideoPopup({ startIndex: initialIndex, trigger });
      }

      handleExternalClick(event) {
        const trigger = event.target.closest("[data-product-video-gallery-trigger]");
        if (!trigger || trigger.dataset.productVideoGalleryTrigger !== this.id) return;

        this.openVideoPopup({ startIndex: 0, trigger });
      }

      openVideoPopup({ startIndex = 0, trigger } = {}) {
        if (!this.videoTemplates[startIndex]) return;

        this.activeTrigger = trigger;
        this.stopAllVideos();
        this.destroySlider();

        const gallery = document.createElement("div");
        gallery.className = "m-product-videos__gallery";
        gallery.tabIndex = -1;
        if (this.videoTemplates.length === 1) gallery.classList.add("m-product-videos__gallery--single");

        const sliderElement = document.createElement("div");
        sliderElement.className = "m-product-videos__slider swiper-container";
        const wrapper = document.createElement("div");
        wrapper.className = "swiper-wrapper";

        this.videoTemplates.forEach((template, index) => {
          const slide = document.createElement("div");
          slide.className = "m-product-videos__slide swiper-slide";
          slide.dataset.videoIndex = index;
          const media = document.createElement("div");
          media.className = "m-product-videos__media";
          slide.appendChild(media);
          wrapper.appendChild(slide);
        });

        sliderElement.appendChild(wrapper);
        gallery.appendChild(sliderElement);

        if (this.videoTemplates.length > 1 && this.navigationTemplate) {
          const controls = this.navigationTemplate.content.firstElementChild.cloneNode(true);
          controls.querySelectorAll("button").forEach((button) => (button.type = "button"));
          gallery.appendChild(controls);
          this.previousButton = controls.querySelector(".m-slider-controls__button-prev");
          this.nextButton = controls.querySelector(".m-slider-controls__button-next");
        }

        this.gallery = gallery;

        this.modal.appendChild(gallery);
        this.modal.setWidth("min(900px, calc(100vw - 40px))");
        this.modal.setSizes(`m-product-videos__modal m-gradient m-color-${this.dataset.colorScheme}`);
        this.modal.open();
        window.removeEventListener("keydown", this.handleKeyDown);
        window.addEventListener("keydown", this.handleKeyDown);

        this.slider = new MinimogLibs.Swiper(sliderElement, {
          slidesPerView: 1,
          initialSlide: startIndex,
          loop: false,
          allowTouchMove: this.videoTemplates.length > 1,
          simulateTouch: true,
          threshold: 10,
          touchStartPreventDefault: false,
          watchOverflow: true,
          navigation: {
            prevEl: this.previousButton,
            nextEl: this.nextButton,
          },
          on: {
            init: (slider) => this.handleVideoSlideChange(slider),
            slideChange: (slider) => this.handleVideoSlideChange(slider),
          },
        });
        gallery.focus({ preventScroll: true });
      }
    }
  );
}
